<template>
  <GenericCard
    :locked="locked"
    has-delete
    :subtitle="subtitle"
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Language"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="6">
          <v-autocomplete
            v-model="section.language_id"
            item-text="name"
            item-value="id"
            :items="optionsLanguages"
            label="Language*"
            outlined
            :error-messages="getErrorMessages($v.section.language_id)"
            @input="$v.section.language_id.$touch()"
            @blur="$v.section.language_id.$touch()"
          />
        </v-col>
        <!-- autocomplete reading -->
        <v-col>
          <v-autocomplete
            v-model="section.reading"
            :items="optionsReading"
            label="Reading Ability"
            outlined
            :error-messages="getErrorMessages($v.section.reading)"
            @input="$v.section.reading.$touch()"
            @blur="$v.section.reading.$touch()"
          />
        </v-col>
        <!-- autocompllete writing -->
        <v-col>
          <v-autocomplete
            v-model="section.writing"
            :items="optionsWriting"
            label="Writing Ability"
            outlined
            :error-messages="getErrorMessages($v.section.writing)"
            @input="$v.section.writing.$touch()"
            @blur="$v.section.writing.$touch()"
          />
        </v-col>
        <!-- autocomplete speaking -->
        <v-col>
          <v-autocomplete
            v-model="section.speaking"
            :items="optionsSpeaking"
            label="Speaking Ability"
            outlined
            :error-messages="getErrorMessages($v.section.speaking)"
            @input="$v.section.speaking.$touch()"
            @blur="$v.section.speaking.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
    <template #header>
      <v-chip
        v-if="section.primary"
        color="success"
        outlined
        label
      >
        <v-icon
          small
          left
        >
          mdi-check
        </v-icon>
        Primary
      </v-chip>
      <v-btn
        v-else
        small
        @click="dialog = true"
      >
        Set Primary
      </v-btn>
    </template>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="290"
    >
      <v-card>
        <v-card-title class="text-h5">
          Set Primary Email
        </v-card-title>
        <v-card-text>Are you sure you want to set <b>{{ languageLabel }}</b> as primary language?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="onSetPrimaryLanguage"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'

import { alertHandler } from '@/alertHandler'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'
import { SHARED_NAMESPACE } from '@/store/shared'

export default {
  name: 'LanguagesCard',
  components: { GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      dialog: false,
      section: {},
    }
  },

  validations () {
    return {
      section: {
        language_id: { required: helpers.withMessage('Language ID is required.', required) },
        primary: {},
        reading: {},
        speaking: {},
        writing: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `languages-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },
    languageLabel () { return this.optionsLanguages?.find(length => length.id === this.section.language_id)?.name },

    locked: {
      get () {
        return this.peLanguagesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeLanguagesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsLanguages () { return this.getCachedDropdown('languages') },
    optionsReading () { return this.getCachedDropdown('learned_languages/reading') },
    optionsSpeaking () { return this.getCachedDropdown('learned_languages/speaking') },
    optionsWriting () { return this.getCachedDropdown('learned_languages/writing') },
    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.languageLabel})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peLanguagesCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeLanguagesCard(this.sectionIdentifier)

    this.getDropdownOptions({ options: false, path: 'languages' })
    this.getDropdownOptions({ path: 'learned_languages/reading' })
    this.getDropdownOptions({ path: 'learned_languages/speaking' })
    this.getDropdownOptions({ path: 'learned_languages/writing' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peLanguagesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peLanguagesCardCreateUndo()
      else {
        return this.peLanguagesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peLanguagesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peLanguagesCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPeLanguagesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async onSetPrimaryLanguage () {
      this.section.primary = true
      this.dialog = false
      return this.onCardUpdate()
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peLanguagesCardCreateUndo',
      'setPeLanguagesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeLanguagesCard',
      'peLanguagesCardCreate',
      'peLanguagesCardDelete',
      'peLanguagesCardRefresh',
      'peLanguagesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
